import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/lib/queryKeys";
import type { ProjectStats } from "@/hooks/useProjectStats";
import type { ProjectCardStats } from "@/components/project/ProjectCard";

function computeGatePassRate(stats: ProjectStats): number {
  const total = stats.gate_pass_rate.pass_count + stats.gate_pass_rate.fail_count;
  if (total === 0) return 0;
  return Math.round((stats.gate_pass_rate.pass_count / total) * 100);
}

function computeLastActive(stats: ProjectStats): string | null {
  const dates = stats.adoption.members
    .map((m) => m.last_active)
    .filter(Boolean);
  if (dates.length === 0) return null;
  return dates.reduce((latest, d) => (d > latest ? d : latest));
}

export function useProjectsStats(slugs: string[]): Record<string, ProjectCardStats> {
  const queries = useQueries({
    queries: slugs.map((slug) => ({
      queryKey: queryKeys.projects.stats(slug),
      queryFn: () => api<ProjectStats>(`/api/v2/projects/${slug}/stats`),
      enabled: slug.length > 0,
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const result: Record<string, ProjectCardStats> = {};
  for (let i = 0; i < slugs.length; i++) {
    const query = queries[i];
    if (query.data) {
      result[slugs[i]] = {
        artifactCoverage: query.data.artifact_coverage_pct,
        gatePassRate: computeGatePassRate(query.data),
        activeMembers: query.data.adoption.active_member_count,
        lastActive: computeLastActive(query.data),
      };
    }
  }
  return result;
}
